import type { Document, Approval } from '@/entities/Document/@x/User';
import type { Branch } from '@/entities/Branch/@x/User';
import type { AppRawRule } from '@/shared/lib/casl';

export enum RoleEnum {
  USER = 'USER',
  SUPERUSER = 'SUPERUSER',
}

export enum UserStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export type UserStatus = 'ACTIVE' | 'INACTIVE';

export interface Position {
  id: number;
  name: string;
}

export interface UserPosition {
  userId: number;
  positionId: number;
  position: Position;
}

export interface UserTrainingSession {
  id: number;
  trainingId: number;
  trainingDate: string;
}

export interface UserTraining {
  id: number;
  createdAt: string;
  updatedAt: string;
  notes: string;
  documentApprovalId: number;
  userId: number;
}

export interface Membership {
  branchId: number;
  branchName: string;
  positionName: string;
  permissions: AppRawRule[];
  functionalActivities?: Array<{
    functionalActivity: {
      id: number;
      name: string;
    };
  }>;
  branch: Branch;
  createdAt: string;
  id: number;
  positionId?: number;
  updatedAt: string;
  userId: number;
  position?: {
    createdAt: string;
    id: number;
    name: string;
    updatedAt: string;
    active?: boolean;
  };
  positions?: { id: number; name: string }[];
}

export interface User {
  id?: number;
  email?: string;
  name?: string;
  username?: string;
  password?: string;
  role?: RoleEnum;
  permissionVersion?: string | null;
  registrationNumber?: string | null;
  admissionDate?: string;
  dismissalDate?: string;
  status?: UserStatus;
  createdAt?: string | null;
  updatedAt?: string | null;
  documentApprovals?: Approval[];
  documents?: Document[];
  trainingSessions?: UserTrainingSession[];
  trainings?: UserTraining[];
  userPositions?: UserPosition[];
  positions?: Position[];
  memberships?: Membership[];
  branches?: Branch[];
  membership?: MembershipItem | null;
  creator?: { id: number; name: string } | null;
  updater?: { id: number; name: string } | null;
}

export interface MembershipItem {
  id: number;
  branchId: number;
  userId: number;
  positionId?: number;
  createdAt: string;
  updatedAt: string;
  positions: {
    id: number;
    name: string;
    active: boolean;
    type: string;
    createdAt: string;
    updatedAt: string;
  }[];
  branch: { id: number; name: string };
  functionalActivities: { functionalActivity: { id: number; name: string } }[];
}

export interface UserByPositionResponse extends Omit<User, 'memberships'> {
  membership: MembershipItem | null;
}

export interface PaginationMetadata {
  total: number;
  page: number;
  itemsPerPage: number;
  totalPages: number;
}

export interface PaginatedUserResponse {
  success: boolean;
  data: User[];
  metadata: PaginationMetadata;
}

export interface UserSearchParams {
  search?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  searchField?: 'name' | 'registration';
  positionId?: number;
  userId?: number;
  departmentId?: number;
  functionalActivityId?: number;
  status?: UserStatus;
  admissionDate?: string;
  dismissalDate?: string;
}
